import React, { useEffect, useRef } from "react";
import Draggable from "react-draggable";
import { IconButton, Tooltip, Box, Paper, Typography } from "@mui/material";
import {
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  CallEnd,
  CloseFullscreen,
  Phone,
  OpenInFull,
} from "@mui/icons-material";
import debugLog from "../../utility/services/debugLog";

// FloatingCallWindow component for Picture-in-Picture (PiP) calls
// This component renders a draggable floating window that persists across page navigation
// Uses fixed positioning to avoid Page Visibility API triggers that would destroy Agora tracks
// NOTE: Duration is not displayed in PiP (compact UI) but is shown in full call screen
const FloatingCallWindowComponent = ({
  // Call state
  isVideoCall,
  isAudioCall,
  showMessage,

  // Call controls
  hide_video,
  onEndCall,
  onExpand,

  // User info
  selectedUser,
  userName, // Saved name from callMetadata (fallback after re-login)

  // Direct track references (from callMetadata)
  localVideoTrack,
  remoteVideoTrack,
  localAudioTrack,
  remoteAudioTrack,
}) => {
  // FIX: Use saved userName FIRST (preserved across navigation), then fall back to selectedUser
  const displayName =
    userName ||
    (selectedUser?.first_name
      ? `${selectedUser.first_name} ${selectedUser.last_name || ""}`.trim()
      : "In call...");

  // Refs for video containers
  const remoteVideoRef = useRef(null);
  const localVideoRef = useRef(null);

  // Detect if user is on mobile device
  const isMobileDevice = window.innerWidth <= 768;

  // Smart PiP sizing: Detect if video is portrait (phone) or landscape (web/desktop)
  // Phone videos are typically 9:16 (portrait), web videos are 16:9 (landscape)
  const isPortraitVideo = remoteVideoTrack
    ? remoteVideoTrack._videoHeight > remoteVideoTrack._videoWidth
    : false;

  // Calculate PiP window dimensions based on video orientation and device
  let windowWidth, windowHeight;

  if (isMobileDevice) {
    // Mobile device: Smaller PiP windows to fit screen
    if (isVideoCall) {
      if (isPortraitVideo) {
        // Portrait video on mobile
        windowWidth = Math.min(200, window.innerWidth - 40);
        windowHeight = Math.min(360, window.innerHeight - 100);
      } else {
        // Landscape video on mobile
        windowWidth = Math.min(280, window.innerWidth - 40);
        windowHeight = Math.min(210, window.innerHeight - 100);
      }
    } else {
      // Audio call on mobile
      windowWidth = Math.min(280, window.innerWidth - 40);
      windowHeight = 80; // Smaller pill for mobile
    }
  } else {
    // Desktop/Tablet: Original sizing logic
    if (isVideoCall) {
      if (isPortraitVideo) {
        // Portrait video (phone): Taller and narrower window
        windowWidth = 280; // Narrower for portrait
        windowHeight = 500; // Taller for portrait (9:16 ratio)
      } else {
        // Landscape video (web): Original dimensions (unchanged)
        windowWidth = 380;
        windowHeight = 450; // Original height for web calls
      }
    } else {
      // Audio call
      windowWidth = 320;
      windowHeight = 280;
    }
  }

  // Calculate default position for bottom-right corner (responsive)
  const padding = isMobileDevice ? 10 : 20;
  const defaultPosition = {
    x: window.innerWidth - windowWidth - padding,
    y: window.innerHeight - windowHeight - padding,
  };

  // Play remote video whenever it changes or component updates
  useEffect(() => {
    if (!isVideoCall || !remoteVideoTrack) {
      return;
    }

    // Wait for element to exist in DOM with retry logic
    let attempts = 0;
    const maxAttempts = 10;
    let timeoutId = null;
    let cancelled = false;

    const tryPlayVideo = () => {
      if (cancelled) return;

      const element = document.getElementById("floating-remote-video");

      if (element && element.tagName === "VIDEO") {
        try {
          // Validate track has play method (prevents errors with stale tracks after re-login)
          if (typeof remoteVideoTrack.play !== "function") {
            console.error(
              "❌ Remote video track is invalid - missing play() method (likely stale after re-login)"
            );
            return;
          }
          remoteVideoTrack.play("floating-remote-video");
        } catch (error) {
          console.error("❌ Error playing remote video in PiP:", error);
        }
      } else {
        attempts++;
        if (attempts < maxAttempts) {
          timeoutId = setTimeout(tryPlayVideo, 100);
        } else {
          console.error(
            "❌ floating-remote-video element not found after max attempts"
          );
        }
      }
    };

    timeoutId = setTimeout(tryPlayVideo, 50);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isVideoCall, remoteVideoTrack]);

  // Play local video whenever it changes or hide_video changes
  useEffect(() => {
    if (!isVideoCall || !localVideoTrack || hide_video) {
      return;
    }

    // Wait for element to exist in DOM with retry logic
    let attempts = 0;
    const maxAttempts = 10;
    let timeoutId = null;
    let cancelled = false;

    const tryPlayVideo = () => {
      if (cancelled) return;

      const element = document.getElementById("floating-local-video");

      if (element && element.tagName === "VIDEO") {
        try {
          // Validate track has play method (prevents errors with stale tracks after re-login)
          if (typeof localVideoTrack.play !== "function") {
            console.error(
              "❌ Local video track is invalid - missing play() method (likely stale after re-login)"
            );
            return;
          }
          localVideoTrack.play("floating-local-video");
        } catch (error) {
          console.error("❌ Error playing local video in PiP:", error);
        }
      } else {
        attempts++;
        if (attempts < maxAttempts) {
          timeoutId = setTimeout(tryPlayVideo, 100);
        } else {
          console.error(
            "❌ floating-local-video element not found after max attempts"
          );
        }
      }
    };

    timeoutId = setTimeout(tryPlayVideo, 50);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isVideoCall, localVideoTrack, hide_video]);

  // Audio call toast-style (draggable)
  if (isAudioCall) {
    const audioWidth = isMobileDevice ? windowWidth : 380;
    const audioDefaultPosition = {
      x: window.innerWidth - audioWidth - padding,
      y: window.innerHeight - (isMobileDevice ? 80 : 80) - padding,
    };

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 0,
          height: 0,
          pointerEvents: "none",
          zIndex: 99999,
        }}
      >
        <Draggable
          handle=".audio-drag-handle"
          defaultPosition={audioDefaultPosition}
        >
          <Paper
            elevation={12}
            className="audio-drag-handle"
            sx={{
              position: "absolute",
              display: "flex",
              alignItems: "center",
              gap: isMobileDevice ? 1 : 1.5,
              px: isMobileDevice ? 1.5 : 2,
              py: isMobileDevice ? 1 : 1.5,
              borderRadius: "50px", // Pill shape
              background:
                "linear-gradient(135deg, rgba(29, 126, 124, 0.95) 0%, rgba(20, 100, 98, 0.95) 100%)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
              backdropFilter: "blur(10px)",
              width: audioWidth,
              cursor: "move",
              pointerEvents: "auto",
            }}
          >
            {/* Phone Icon */}
            <Phone
              sx={{
                color: "white",
                fontSize: isMobileDevice ? 18 : 20,
                flexShrink: 0,
              }}
            />

            {/* Status Message or Audio Visualizer */}
            {remoteAudioTrack ||
            userName ||
            (displayName && displayName !== "In call...") ? (
              // Audio Visualizer - Show when we have user name (call is active)
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  height: 24,
                  maxWidth: 200,
                  minWidth: 0,
                }}
              >
                {[...Array(8)].map((_, index) => {
                  const heights = [
                    "6px",
                    "14px",
                    "10px",
                    "18px",
                    "8px",
                    "16px",
                    "12px",
                    "20px",
                  ];
                  const durations = [
                    "0.4s",
                    "0.5s",
                    "0.6s",
                    "0.45s",
                    "0.55s",
                    "0.5s",
                    "0.6s",
                    "0.48s",
                  ];
                  return (
                    <Box
                      key={index}
                      sx={{
                        width: 2.5,
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        borderRadius: "1px",
                        flexShrink: 0,
                        animation: `audioBar${index} ${durations[index]} ease-in-out infinite alternate`,
                        [`@keyframes audioBar${index}`]: {
                          "0%": {
                            height: "4px",
                          },
                          "100%": {
                            height: heights[index],
                          },
                        },
                      }}
                    />
                  );
                })}
                <Typography
                  variant="body2"
                  sx={{
                    color: "white",
                    fontWeight: 500,
                    fontSize: isMobileDevice ? "10px" : "13px",
                    ml: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                    maxWidth: isMobileDevice ? 80 : 140,
                  }}
                >
                  In call...
                </Typography>
              </Box>
            ) : (
              // Status Message - Show when waiting
              <Typography
                variant="body2"
                sx={{
                  color: "white",
                  fontWeight: 600,
                  fontSize: isMobileDevice ? "10px" : "14px",
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  wordBreak: "break-word",
                  maxWidth: isMobileDevice ? 120 : 200,
                  minWidth: 0,
                }}
              >
                {showMessage || "Calling..."}
              </Typography>
            )}

            {/* Spacer to push buttons to the right */}
            <Box sx={{ flex: 1 }} />

            {/* End Call Button */}
            <Tooltip title="End call">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onEndCall();
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onEndCall();
                }}
                size="small"
                sx={{
                  backgroundColor: "#f44336",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#d32f2f",
                  },
                  "&:active": {
                    backgroundColor: "#b71c1c",
                  },
                  width: isMobileDevice ? 28 : 32,
                  height: isMobileDevice ? 28 : 32,
                  flexShrink: 0,
                  cursor: "pointer",
                  pointerEvents: "auto",
                  touchAction: "manipulation",
                }}
              >
                <CallEnd sx={{ fontSize: isMobileDevice ? 14 : 16 }} />
              </IconButton>
            </Tooltip>

            {/* Expand Button */}
            <Tooltip title="Expand">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();

                  debugLog("FloatingCallWindow_Expand_Click_START", {
                    event: "onClick",
                    timestamp: new Date().toISOString(),
                  });

                  try {
                    debugLog("FloatingCallWindow_Expand_Click", {
                      event: "onClick",
                      isVideoCall,
                      isAudioCall,
                      userName,
                      displayName,
                      hasLocalVideo: !!localVideoTrack,
                      hasRemoteVideo: !!remoteVideoTrack,
                      hasOnExpand: !!onExpand,
                      onExpandType: typeof onExpand,
                    });

                    if (onExpand) {
                      debugLog("FloatingCallWindow_Calling_onExpand", {
                        message: "About to call onExpand()",
                      });
                      onExpand();

                      debugLog("FloatingCallWindow_onExpand_Called", {
                        message: "onExpand() completed successfully",
                      });
                    } else {
                      console.error("🔴 onExpand is not defined!");
                      debugLog.error(
                        "FloatingCallWindow_Expand_Click",
                        "onExpand is not defined!"
                      );
                    }
                  } catch (err) {
                    console.error("🔴 ERROR in expand handler:", err);
                    debugLog.error("FloatingCallWindow_Expand_Click", err, {
                      event: "onClick",
                    });
                  }
                }}
                onTouchEnd={(e) => {
                  try {
                    e.stopPropagation();
                    e.preventDefault();
                    debugLog("FloatingCallWindow_Expand_Touch", {
                      event: "onTouchEnd",
                      isVideoCall,
                      isAudioCall,
                    });
                    if (onExpand) {
                      onExpand();
                    }
                  } catch (err) {
                    debugLog.error("FloatingCallWindow_Expand_Touch", err);
                  }
                }}
                size="small"
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.25)",
                  },
                  "&:active": {
                    backgroundColor: "rgba(255, 255, 255, 0.35)",
                  },
                  width: isMobileDevice ? 28 : 32,
                  height: isMobileDevice ? 28 : 32,
                  flexShrink: 0,
                  cursor: "pointer",
                  pointerEvents: "auto",
                  touchAction: "manipulation",
                }}
              >
                <OpenInFull sx={{ fontSize: isMobileDevice ? 14 : 16 }} />
              </IconButton>
            </Tooltip>
          </Paper>
        </Draggable>
      </div>
    );
  }

  // Video call draggable window
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: 0,
        height: 0,
        pointerEvents: "none",
        zIndex: 99999,
      }}
    >
      <Draggable handle=".drag-handle" defaultPosition={defaultPosition}>
        <Paper
          elevation={24}
          sx={{
            position: "absolute",
            width: windowWidth,
            height: windowHeight,
            pointerEvents: "auto",
            borderRadius: 3,
            overflow: "hidden",
            background:
              "linear-gradient(145deg, rgba(20, 20, 25, 0.98) 0%, rgba(40, 40, 45, 0.95) 100%)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
            backdropFilter: "blur(20px)",
            display: "flex",
            flexDirection: "column",
            transition: "width 0.3s ease, height 0.3s ease", // Smooth resize animation
          }}
        >
          {/* Header - Drag Handle */}
          <Box
            className="drag-handle"
            sx={{
              p: 1.5,
              backgroundColor: "rgba(29, 126, 124, 0.15)",
              borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
              cursor: "move",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isVideoCall ? (
                <Videocam sx={{ color: "#1d7e7c", fontSize: 20 }} />
              ) : (
                <Phone sx={{ color: "#1d7e7c", fontSize: 20 }} />
              )}
              <Typography
                variant="body2"
                sx={{
                  color: "white",
                  fontWeight: 600,
                  fontSize: "13px",
                }}
              >
                {isVideoCall ? "Video Call" : "Audio Call"}
              </Typography>
            </Box>

            {/* Exit PiP button */}
            <Tooltip title="Expand">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onExpand();
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onExpand();
                }}
                size="small"
                sx={{
                  color: "white",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                  },
                  "&:active": {
                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                  },
                  width: 28,
                  height: 28,
                  pointerEvents: "auto",
                  touchAction: "manipulation",
                }}
              >
                <OpenInFull sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Video/Audio Content Area */}
          <Box
            sx={{
              flex: 1,
              position: "relative",
              backgroundColor: "#000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              minHeight: 0, // Allow flex item to shrink below content size
            }}
          >
            {isVideoCall ? (
              <>
                {/* Remote Video - Full size */}
                <video
                  id="floating-remote-video"
                  ref={remoteVideoRef}
                  autoPlay
                  playsInline
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain", // Show full video without cropping
                    backgroundColor: "#000",
                  }}
                />

                {/* Local Video - Small overlay */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: isMobileDevice ? 50 : 60, // Adjusted for mobile
                    right: isMobileDevice ? 8 : 10,
                    width: isMobileDevice ? 60 : isPortraitVideo ? 80 : 100, // Smaller on mobile
                    height: isMobileDevice ? 45 : isPortraitVideo ? 60 : 75,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: isMobileDevice
                      ? "1px solid rgba(255, 255, 255, 0.2)"
                      : "2px solid rgba(255, 255, 255, 0.2)",
                    backgroundColor: "#000",
                    zIndex: 20, // Higher z-index to stay above control bar
                  }}
                >
                  {!hide_video ? (
                    <video
                      id="floating-local-video"
                      ref={localVideoRef}
                      autoPlay
                      playsInline
                      muted
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <VideocamOff sx={{ color: "rgba(255,255,255,0.5)" }} />
                    </Box>
                  )}
                </Box>

                {/* Waiting room message overlay (shown when no remote video) */}
                {!remoteVideoTrack && showMessage && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "45%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.5,
                      zIndex: 1,
                      textAlign: "center",
                      px: 2,
                      maxWidth: "90%",
                    }}
                  >
                    <Videocam
                      sx={{
                        fontSize: isMobileDevice ? 28 : 40,
                        color: "#1d7e7c",
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: "white",
                        fontSize: isMobileDevice ? "9px" : "12px",
                        fontWeight: 500,
                        lineHeight: 1.3,
                      }}
                    >
                      {showMessage}
                    </Typography>
                  </Box>
                )}

                {/* Control Bar Overlay - Positioned on top of video */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: isMobileDevice ? 1 : 1.5,
                    background:
                      "linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.7) 70%, transparent 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    zIndex: 10, // Above video but below local video
                  }}
                >
                  {/* End Call */}
                  <Tooltip title="End call">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onEndCall();
                      }}
                      onTouchEnd={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onEndCall();
                      }}
                      sx={{
                        backgroundColor: "#f44336",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "#d32f2f",
                        },
                        "&:active": {
                          backgroundColor: "#b71c1c",
                        },
                        width: isMobileDevice ? 32 : 38,
                        height: isMobileDevice ? 32 : 38,
                        pointerEvents: "auto",
                        touchAction: "manipulation",
                      }}
                    >
                      <CallEnd sx={{ fontSize: isMobileDevice ? 16 : 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </>
            ) : (
              // Audio call - Show icon and caller name
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  p: 3,
                }}
              >
                <Phone sx={{ fontSize: 60, color: "#1d7e7c" }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255, 255, 255, 0.8)",
                    textAlign: "center",
                    fontSize: "13px",
                  }}
                >
                  {displayName || "In call..."}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Draggable>
    </div>
  );
};

// Custom comparison function for React.memo
// Return true if props are equal (skip re-render)
// Return false if props are different (re-render)
const arePropsEqual = (prevProps, nextProps) => {
  const checks = {
    isVideoCall: prevProps.isVideoCall === nextProps.isVideoCall,
    isAudioCall: prevProps.isAudioCall === nextProps.isAudioCall,
    showMessage: prevProps.showMessage === nextProps.showMessage,
    hide_video: prevProps.hide_video === nextProps.hide_video,
    callDuration: prevProps.callDuration === nextProps.callDuration,
    localVideoTrack: prevProps.localVideoTrack === nextProps.localVideoTrack,
    remoteVideoTrack: prevProps.remoteVideoTrack === nextProps.remoteVideoTrack,
    localAudioTrack: prevProps.localAudioTrack === nextProps.localAudioTrack,
    remoteAudioTrack: prevProps.remoteAudioTrack === nextProps.remoteAudioTrack,
  };

  const areEqual = Object.values(checks).every((check) => check === true);

  return areEqual;
};

// Wrap with React.memo to prevent re-renders when parent (App.js) re-renders due to unrelated state changes
const FloatingCallWindow = React.memo(
  FloatingCallWindowComponent,
  arePropsEqual
);

export default FloatingCallWindow;
